import { FieldKey } from '../../context/field-keys.js';
/**
 * Net rentable area from available space + area-measurement-type, ported from the FE listener
 * `src/app/records/controls/common/listeners/space/auto-calculation-space-information-section.ts`
 * (`calculateNetRentableArea` + `getEfficiencyRate`).
 *
 * Dispatch on the area-measurement-type string, lower-cased, `String.includes` — EXACTLY as the
 * legacy (`SpaceAreaTypes` tokens). Order matters and is preserved:
 *   1. contains 'net'                 → passthrough (size is already net)
 *      ('Net Lettable Area' contains both 'net' and 'lettable' — NET wins because it is checked first)
 *   2. contains 'gross' or 'lettable' → size × (efficiencyRate / 100); efficiency rate is REQUIRED
 *      and must be > 0, otherwise the output clears to null
 *   3. anything else                  → passthrough
 * The result is rounded to the nearest WHOLE number via `Math.round` — that whole-number rounding
 * is part of the legacy formula (not the host 2dp rounding pipeline), so `deps.round` is not used.
 *
 * Clearing semantics kept from the legacy (it patches null): missing/non-positive size, missing
 * type, or a gross/lettable type without a positive efficiency rate all yield null.
 * Host-owned, NOT modelled here: dirty/touched gating, and the UOM copy
 * (net_rentable_area_uom mirrors available_space_uom — T2.7 host propagation).
 */
/** Area-measurement-type tokens (legacy `SpaceAreaTypes`), matched case-insensitively. */
export var SpaceAreaType = /*#__PURE__*/function (SpaceAreaType) {
  SpaceAreaType["Net"] = "net";
  SpaceAreaType["Gross"] = "gross";
  SpaceAreaType["Lettable"] = "lettable";
  return SpaceAreaType;
}(SpaceAreaType || {});
/**
 * Bare kernel. Returns null wherever the legacy listener cleared the field.
 * @param spaceSize available space (any UOM — the rule is UOM-blind)
 * @param areaMeasurementType `area_measurement_type_desc` display string
 * @param efficiencyRate space utilization efficiency rate in percent (e.g. 85 = 85%)
 */
export function calculateNetRentableArea(spaceSize, areaMeasurementType, efficiencyRate) {
  if (!spaceSize || spaceSize <= 0) {
    return null;
  }
  if (!areaMeasurementType) {
    return null;
  }
  const areaType = areaMeasurementType.toLowerCase();
  let netRentableAreaValue;
  if (areaType.includes(SpaceAreaType.Net)) {
    netRentableAreaValue = spaceSize;
  } else if (areaType.includes(SpaceAreaType.Gross) || areaType.includes(SpaceAreaType.Lettable)) {
    // Legacy getEfficiencyRate: value present and > 0, else null → clear.
    const rate = efficiencyRate != null && Number(efficiencyRate) > 0 ? Number(efficiencyRate) : null;
    if (rate === null) {
      return null;
    }
    netRentableAreaValue = spaceSize * (rate / 100);
  } else {
    netRentableAreaValue = spaceSize;
  }
  // Legacy rounds off the calculated value to the nearest whole number.
  return Math.round(netRentableAreaValue);
}
/** Net rentable area from available space, area-measurement-type and efficiency rate. */
export const netRentableAreaRule = {
  id: 'space.net-rentable-area.from-area-measurement-type',
  inputs: [FieldKey.SpaceAvailableSpace, FieldKey.SpaceAreaMeasurementType, FieldKey.SpaceUtilizationEfficiencyRate],
  outputs: [FieldKey.SpaceNetRentableArea],
  formulaVersion: '1.0.0',
  run: ctx => {
    const size = ctx.get(FieldKey.SpaceAvailableSpace);
    const areaType = ctx.get(FieldKey.SpaceAreaMeasurementType);
    const efficiencyRate = ctx.get(FieldKey.SpaceUtilizationEfficiencyRate);
    const value = calculateNetRentableArea(Number(size ?? 0), String(areaType ?? ''), efficiencyRate);
    ctx.set(FieldKey.SpaceNetRentableArea, value);
  }
};
